/**
 * Whether this page is being used from inside Shopify, where it belongs.
 *
 * The app is an embedded Shopify app: every screen assumes a merchant who
 * arrived through their admin, and the API will not answer without a session
 * token that only App Bridge can mint. Opened directly it therefore renders a
 * shell that can never load anything — which looks broken, and which Shopify
 * reviews against.
 *
 * Three things are allowed through, and each for its own reason rather than as
 * a general escape hatch:
 *
 *   • the legal pages, because Shopify requires a privacy policy at a URL
 *     anyone can open, including people who have not installed anything;
 *   • admin impersonation, which is how support opens a merchant's view from
 *     the internal panel and is authenticated by its own login;
 *   • anything under /admin, which is that panel and is a separate app
 *     entirely — it is not mounted by this tree at all.
 */
import { adminImpersonationToken } from './adminSession'

export const PUBLIC_PATHS = ['/privacy', '/terms']

export function isPublicPath(pathname) {
  const p = String(pathname || '').replace(/\/$/, '') || '/'
  return PUBLIC_PATHS.includes(p)
}

/**
 * `host` is Shopify's own base64 handle for the admin frame. Its presence is
 * the strongest signal: Shopify puts it on the URL when it opens the app, and
 * it is remembered for the session afterwards because React Router strips it.
 *
 * Being inside an iframe is the weaker second signal, and is kept because a
 * page that has navigated within the admin can lose the parameter while
 * remaining genuinely embedded.
 */
export function isEmbeddedContext({ host, pathname, search, inIframe } = {}) {
  if (isPublicPath(pathname)) return true
  if (String(pathname || '').startsWith('/admin')) return true

  const params = new URLSearchParams(search || '')
  if (params.get('admin_impersonate') === '1') return true

  // Remembered for the session, exactly as `host` is, and for the same reason:
  // the admin panel starts impersonation by putting admin_impersonate=1 on the
  // url, then the app's own first navigation — to /dashboard — strips the
  // query. From that moment the parameter is gone while the session is still
  // very much an admin one, so the seller app decided it was not embedded and
  // drew the "open from your Shopify admin" gate underneath the ADMIN VIEW
  // banner that was saying which shop was being viewed.
  //
  // The token is the durable marker: the admin panel sets it before rendering
  // the seller app and clears it on "Back to Admin", so its presence means an
  // impersonation session is open regardless of what the url has been rewritten
  // to since.
  // Through the one resolver, which also finds the admin's own token in
  // localStorage — a portal opened in a new tab does not inherit
  // sessionStorage, so the per-tab copy is absent there. See
  // utils/adminSession.
  if (adminImpersonationToken()) return true

  if (host) return true
  if (params.get('shop')) return true   // mid-install, before host is handed over
  return !!inIframe
}
